import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import spformsApi from '../lib/spformsApi';

const STORAGE_KEY = 'spforms_auth';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function initializeAuth() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          if (isMounted) setLoading(false);
          return;
        }

        const parsed = JSON.parse(stored);
        if (!parsed?.token) {
          if (isMounted) setLoading(false);
          return;
        }

        // Validate the session with the server
        const result = await spformsApi.authValidateSession(parsed.token);
        
        if (!isMounted) return;

        if (result?.valid) {
          const updatedSession = { ...parsed, ...result };
          setSession(updatedSession);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setSession(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // On error, we must still stop loading to let the app show the login screen
        if (isMounted) setSession(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    initializeAuth();
    return () => { isMounted = false; };
  }, []);

  const login = async ({ email, password }) => {
    const result = await spformsApi.authLogin(email, password);
    const sessionData = {
      token: result.token,
      user_id: result.user_id,
      tenant_id: result.tenant_id,
      email: result.email,
      name: result.name,
      role: result.role,
      expires_at: result.expires_at,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
    setSession(sessionData);
    return sessionData;
  };

  const logout = async () => {
    try {
      if (session?.token) await spformsApi.authLogout(session.token);
    } catch (e) {
      console.warn('Logout failed', e);
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      setSession(null);
    }
  };

  const value = useMemo(() => ({
    loading,
    session,
    isAuthenticated: !!session?.token,
    login,
    logout
  }), [loading, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}